import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../services/supabaseClient';
import { LoadingSpinner, EmptyState, PageHeader, FilterBar, StatusBadge } from '../../../components/ui';
import { useAuth } from '../../../context/AuthContext';

const StaffPage = () => {
  const [activeTab, setActiveTab] = useState('directory');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('all');
  const [toast, setToast] = useState(null);

  const { role: currentRole } = useAuth();
  const isAdmin = currentRole === 'admin';
  const isManager = currentRole === 'manager';
  const canViewTeams = isAdmin || isManager;
  const canManageInvites = isAdmin;

  const [roles, setRoles] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleForm, setRoleForm] = useState({ role_key: '', name: '', description: '' });
  const [roleEdits, setRoleEdits] = useState({});
  const [permissionForms, setPermissionForms] = useState({});
  const [directoryEdits, setDirectoryEdits] = useState({});

  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamForm, setTeamForm] = useState({ team_key: '', name: '', description: '' });
  const [teamEdits, setTeamEdits] = useState({});
  const [teamMemberAdds, setTeamMemberAdds] = useState({});

  const [inviteForm, setInviteForm] = useState({
    auth_user_id: '',
    email: '',
    name: '',
    role_key: 'cro'
  });
  const [inviteLoading, setInviteLoading] = useState(false);

  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [addStaffForm, setAddStaffForm] = useState({
    email: '',
    name: '',
    phone: '',
    password: '',
    role_key: 'cro',
    team: '',
    locale: 'en'
  });
  const [addStaffLoading, setAddStaffLoading] = useState(false);

  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilters, setActivityFilters] = useState({ staff: 'all', event: 'all' });
  const [activityPage, setActivityPage] = useState(1);
  const [activityPageSize, setActivityPageSize] = useState(20);
  const [activityTotal, setActivityTotal] = useState(0);

  const showToast = (type, text) => {
    setToast({ type, text, id: Date.now() });
    window.clearTimeout(showToast._timer);
    showToast._timer = window.setTimeout(() => setToast(null), 3000);
  };

  const loadRolesPermissions = async () => {
    if (!isAdmin) {
      setRoles([]);
      setRolePermissions([]);
      return;
    }
    try {
      setRolesLoading(true);
      const [{ data: roleData, error: roleError }, { data: permData, error: permError }] = await Promise.all([
        supabase.from('staff_roles').select('*').order('role_key'),
        supabase.from('role_permissions').select('*').order('resource')
      ]);
      if (roleError) throw roleError;
      if (permError) throw permError;
      setRoles(roleData || []);
      setRolePermissions(permData || []);
    } catch (err) {
      console.error('roles load error', err);
      showToast('error', 'Failed to load roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const loadTeams = async () => {
    if (!canViewTeams) {
      setTeams([]);
      return;
    }
    try {
      setTeamsLoading(true);
      const { data, error } = await supabase
        .from('staff_teams')
        .select('id, team_key, name, description, is_active, created_at, updated_at, team_members:team_members(id, staff_id, role_in_team, is_primary, staff_profiles:staff_profiles(id, name, email, role_key))')
        .order('name', { ascending: true });
      if (error) throw error;
      setTeams(data || []);
    } catch (err) {
      console.error('teams load error', err);
      showToast('error', 'Failed to load teams');
    } finally {
      setTeamsLoading(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      let q = supabase.from('staff_profiles').select('id, name, email, phone, role_key, team, active, is_active, locale, updated_at');
      if (role !== 'all') q = q.eq('role_key', role);
      const { data, error } = await q;
      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      setError('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = async ({
    page = activityPage,
    pageSize = activityPageSize,
    nextFilters = activityFilters
  } = {}) => {
    if (!isAdmin) {
      setActivityLogs([]);
      setActivityTotal(0);
      setActivityLoading(false);
      return;
    }
    try {
      setActivityLoading(true);
      setActivityFilters(nextFilters);

      const from = Math.max(0, (page - 1) * pageSize);
      const to = from + pageSize - 1;

      let query = supabase
        .from('staff_activity_logs')
        .select('id, staff_id, event, metadata, created_at, staff_profiles:staff_profiles!left(id, name, email, role_key)', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (nextFilters.staff && nextFilters.staff !== 'all') {
        query = query.eq('staff_id', nextFilters.staff);
      }
      if (nextFilters.event && nextFilters.event !== 'all') {
        query = query.eq('event', nextFilters.event);
      }

      const { data, count, error } = await query;
      if (error) throw error;
      setActivityLogs(data || []);
      setActivityTotal(count || 0);
      setActivityPage(page);
      setActivityPageSize(pageSize);
    } catch (err) {
      console.error('activity logs load error', err);
      showToast('error', 'Failed to load activity logs');
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => { load(); }, [role]);
  useEffect(() => {
    loadTeams();
    if (isAdmin) {
      loadRolesPermissions();
      loadActivityLogs();
    } else {
      setRoles([]);
      setRolePermissions([]);
      setActivityLogs([]);
      setActivityTotal(0);
      setActivityLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, canViewTeams]);

  const tabs = useMemo(() => {
    const base = [{ key: 'directory', label: 'Directory' }];
    if (canViewTeams) {
      base.push({ key: 'teams', label: 'Teams' });
    }
    if (isAdmin) {
      base.push(
        { key: 'roles', label: 'Roles & Permissions' },
        { key: 'activity', label: 'Activity Logs' },
        { key: 'invites', label: 'Invite / Activate' }
      );
    }
    return base;
  }, [canViewTeams, isAdmin]);

  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.find(tab => tab.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
  }, [tabs, activeTab]);

  const filtered = rows.filter(r => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = `${r.name || ''} ${r.email || ''} ${r.phone || ''} ${r.team || ''}`.toLowerCase();
    return hay.includes(q);
  });

  const roleSelectOptions = useMemo(() => {
    if (roles.length) {
      return roles.map(r => ({ value: r.role_key, label: r.name || r.role_key }));
    }
    return [
      { value: 'guest', label: 'Guest' },
      { value: 'cro', label: 'CRO' },
      { value: 'docs', label: 'Documentation' },
      { value: 'visa', label: 'Visa' },
      { value: 'agreements', label: 'Agreements' },
      { value: 'education', label: 'Education' },
      { value: 'manager', label: 'Manager' },
      { value: 'admin', label: 'Admin' }
    ];
  }, [roles]);

  const beginStaffEdit = (staff) => {
    setDirectoryEdits(prev => ({
      ...prev,
      [staff.id]: {
        role_key: staff.role_key || '',
        team: staff.team || '',
        phone: staff.phone || '',
        locale: staff.locale || ''
      }
    }));
  };

  const cancelStaffEdit = (staffId) => {
    setDirectoryEdits(prev => {
      const next = { ...prev };
      delete next[staffId];
      return next;
    });
  };

  const saveStaffEdit = async (staff) => {
    const edits = directoryEdits[staff.id];
    if (!edits) return;
    try {
      const payload = {
        role_key: edits.role_key || null,
        team: edits.team?.trim() || null,
        phone: edits.phone?.trim() || null,
        locale: edits.locale?.trim() || null,
        updated_at: new Date().toISOString()
      };
      const { error } = await supabase.from('staff_profiles').update(payload).eq('id', staff.id);
      if (error) throw error;
      await supabase.rpc('admin_log_staff_event', {
        p_staff_id: staff.id,
        p_event: 'profile_updated',
        p_metadata: {
          role_key: payload.role_key,
          team: payload.team,
          phone: payload.phone,
          locale: payload.locale,
          updated_by: 'admin'
        }
      }).catch(() => { });
      showToast('success', 'Staff profile updated');
      cancelStaffEdit(staff.id);
      await load();
    } catch (err) {
      console.error('staff update error', err);
      showToast('error', err.message || 'Failed to update staff');
    }
  };

  const toggleStaffActive = async (staff) => {
    try {
      if (staff.is_active) {
        const { error } = await supabase.rpc('admin_offboard_staff', {
          p_staff_id: staff.id,
          p_notes: 'Offboarded via Staff directory'
        });
        if (error) throw error;
        showToast('success', 'Staff offboarded');
      } else {
        const { error } = await supabase
          .from('staff_profiles')
          .update({ is_active: true, updated_at: new Date().toISOString() })
          .eq('id', staff.id);
        if (error) throw error;
        await supabase.rpc('admin_log_staff_event', {
          p_staff_id: staff.id,
          p_event: 'reinstated',
          p_metadata: { updated_by: 'admin' }
        }).catch(() => { });
        showToast('success', 'Staff reinstated');
      }
      await load();
    } catch (err) {
      console.error('toggle active error', err);
      showToast('error', err.message || 'Failed to update status');
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    if (!addStaffForm.email || !addStaffForm.name || !addStaffForm.role_key || !addStaffForm.password) {
      showToast('error', 'Email, name, role, and password are required');
      return;
    }

    try {
      setAddStaffLoading(true);

      // Use Edge Function which has service role access
      const { data, error } = await supabase.functions.invoke('admin-create-staff', {
        body: {
          email: addStaffForm.email.trim(),
          name: addStaffForm.name.trim(),
          role_key: addStaffForm.role_key,
          password: addStaffForm.password,
          phone: addStaffForm.phone?.trim() || null,
          team: addStaffForm.team?.trim() || null,
          locale: addStaffForm.locale?.trim() || 'en'
        }
      });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.error || data.message || 'Failed to add staff member');
      }

      showToast('success', 'Staff member added successfully with login access');
      setShowAddStaffModal(false);
      setAddStaffForm({
        email: '',
        name: '',
        phone: '',
        password: '',
        role_key: 'cro',
        team: '',
        locale: 'en'
      });
      await load();
    } catch (err) {
      console.error('add staff error', err);
      showToast('error', err.message || 'Failed to add staff member');
    } finally {
      setAddStaffLoading(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading staff..." />;

  return (
    <div className="bg-gray-900 font-mono text-white p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">👥 Staff Management</h1>
        <p className="text-gray-400">Manage staff members and their roles</p>
      </div>

      {toast && (
        <div
          key={toast.id}
          className={`alert alert-${toast.type === 'error' ? 'error' : toast.type === 'success' ? 'success' : 'info'}`}
        >
          {toast.text}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center gap-2 flex-wrap mb-6 border-b border-gray-700 pb-2">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`tab-button ${activeTab === tab.key ? 'tab-button-active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeTab === 'directory' && (
        <>
          <div className="mb-4 flex justify-between items-center">
            <FilterBar
              searchPlaceholder="Search name/email/phone..."
              searchValue={search}
              onSearchChange={setSearch}
              filters={[
                {
                  key: 'role',
                  label: 'Filter by role',
                  value: role,
                  options: [
                    { value: 'all', label: 'All roles' },
                    { value: 'cro', label: 'CRO' },
                    { value: 'docs', label: 'Documentation' },
                    { value: 'visa', label: 'Visa' },
                    { value: 'agreements', label: 'Agreements' },
                    { value: 'education', label: 'Education' },
                    { value: 'manager', label: 'Manager' },
                    { value: 'admin', label: 'Admin' },
                    { value: 'guest', label: 'Guest' }
                  ]
                }
              ]}
              onFilterChange={(key, value) => {
                if (key === 'role') setRole(value);
              }}
            />
            {isAdmin && (
              <button
                onClick={() => setShowAddStaffModal(true)}
                className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Staff
              </button>
            )}
          </div>
          <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Team</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Updated</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12">
                      <EmptyState
                        icon="👥"
                        title="No staff members found"
                        description="Try adjusting your search or filter criteria"
                      />
                    </td>
                  </tr>
                ) : (
                  filtered.map(s => (
                    <tr key={s.id} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-white">{s.name || s.email || s.id}</div>
                        <div className="text-xs text-gray-400">{s.locale || 'locale: default'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{s.email || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {directoryEdits[s.id] ? (
                          <input
                            className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-xs"
                            value={directoryEdits[s.id].phone}
                            onChange={(e) =>
                              setDirectoryEdits(prev => ({
                                ...prev,
                                [s.id]: { ...prev[s.id], phone: e.target.value }
                              }))
                            }
                          />
                        ) : (
                          s.phone || '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {directoryEdits[s.id] ? (
                          <select
                            className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-xs"
                            value={directoryEdits[s.id].role_key}
                            onChange={(e) =>
                              setDirectoryEdits(prev => ({
                                ...prev,
                                [s.id]: { ...prev[s.id], role_key: e.target.value }
                              }))
                            }
                          >
                            {roleSelectOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {s.role_key || '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {directoryEdits[s.id] ? (
                          <input
                            className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-xs"
                            value={directoryEdits[s.id].team}
                            onChange={(e) =>
                              setDirectoryEdits(prev => ({
                                ...prev,
                                [s.id]: { ...prev[s.id], team: e.target.value }
                              }))
                            }
                          />
                        ) : (
                          s.team || '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={(s.active ?? s.is_active) ? 'active' : 'inactive'}>
                          {(s.active ?? s.is_active) ? '✓ Active' : '✕ Inactive'}
                        </StatusBadge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {s.updated_at ? new Date(s.updated_at).toLocaleString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-xs space-x-2">
                        {directoryEdits[s.id] ? (
                          <>
                            <button
                              onClick={() => saveStaffEdit(s)}
                              className="px-2 py-1 bg-green-600 text-white rounded"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => cancelStaffEdit(s.id)}
                              className="px-2 py-1 border rounded"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => beginStaffEdit(s)}
                              className="px-2 py-1 border rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => toggleStaffActive(s)}
                              className={`px-2 py-1 border rounded ${s.is_active ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}
                            >
                              {s.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'roles' && isAdmin && (
        <RolesManager
          roles={roles}
          rolesLoading={rolesLoading}
          rolePermissions={rolePermissions}
          roleForm={roleForm}
          setRoleForm={setRoleForm}
          roleEdits={roleEdits}
          setRoleEdits={setRoleEdits}
          permissionForms={permissionForms}
          setPermissionForms={setPermissionForms}
          showToast={showToast}
          refreshRoles={async () => {
            await Promise.all([load(), loadRolesPermissions()]);
          }}
        />
      )}

      {activeTab === 'roles' && !isAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
          You need admin privileges to manage roles and permissions.
        </div>
      )}

      {activeTab === 'teams' && canViewTeams && (
        <TeamsManager
          teams={teams}
          teamsLoading={teamsLoading}
          teamForm={teamForm}
          setTeamForm={setTeamForm}
          teamEdits={teamEdits}
          setTeamEdits={setTeamEdits}
          staffOptions={rows}
          teamMemberAdds={teamMemberAdds}
          setTeamMemberAdds={setTeamMemberAdds}
          showToast={showToast}
          refreshTeams={loadTeams}
          canManageTeams={isAdmin}
        />
      )}

      {activeTab === 'teams' && !canViewTeams && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
          You do not have access to team information.
        </div>
      )}

      {activeTab === 'activity' && isAdmin && (
        <ActivityLogPanel
          logs={activityLogs}
          loading={activityLoading}
          filters={activityFilters}
          setFilters={setActivityFilters}
          page={activityPage}
          setPage={setActivityPage}
          pageSize={activityPageSize}
          setPageSize={setActivityPageSize}
          total={activityTotal}
          staffOptions={rows}
          reload={loadActivityLogs}
          showToast={showToast}
        />
      )}

      {activeTab === 'activity' && !isAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
          Activity logs are restricted to administrators.
        </div>
      )}

      {activeTab === 'invites' && isAdmin && (
        <InvitePanel
          inviteForm={inviteForm}
          setInviteForm={setInviteForm}
          inviteLoading={inviteLoading}
          setInviteLoading={setInviteLoading}
          showToast={showToast}
          onSuccess={load}
          canManageInvites={isAdmin}
        />
      )}

      {activeTab === 'invites' && !isAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
          Only administrators can onboard or activate staff.
        </div>
      )}

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full border border-gray-700">
            <div className="border-b border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Add New Staff Member</h2>
                  <p className="text-sm text-gray-400 mt-1">
                    Creates staff profile with immediate login access
                  </p>
                </div>
                <button
                  onClick={() => setShowAddStaffModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <form onSubmit={handleAddStaff} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John Doe"
                    value={addStaffForm.name}
                    onChange={(e) => setAddStaffForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    required
                    className="w-full bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter a secure password"
                    value={addStaffForm.password}
                    onChange={(e) => setAddStaffForm(prev => ({ ...prev, password: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="john@example.com"
                    value={addStaffForm.email}
                    onChange={(e) => setAddStaffForm(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    className="w-full bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1234567890"
                    value={addStaffForm.phone}
                    onChange={(e) => setAddStaffForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    className="w-full bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={addStaffForm.role_key}
                    onChange={(e) => setAddStaffForm(prev => ({ ...prev, role_key: e.target.value }))}
                  >
                    {roleSelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Team
                  </label>
                  <input
                    type="text"
                    className="w-full bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., CRO Team"
                    value={addStaffForm.team}
                    onChange={(e) => setAddStaffForm(prev => ({ ...prev, team: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Locale
                  </label>
                  <select
                    className="w-full bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={addStaffForm.locale}
                    onChange={(e) => setAddStaffForm(prev => ({ ...prev, locale: e.target.value }))}
                  >
                    <option value="en">English</option>
                    <option value="si">Sinhala</option>
                    <option value="ta">Tamil</option>
                  </select>
                </div>
              </div>


              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowAddStaffModal(false)}
                  className="px-4 py-2 text-sm border border-gray-600 text-gray-300 rounded hover:bg-gray-700 transition-colors"
                  disabled={addStaffLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={addStaffLoading}
                >
                  {addStaffLoading ? 'Adding...' : 'Add Staff Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const RolesManager = ({
  roles,
  rolesLoading,
  rolePermissions,
  roleForm,
  setRoleForm,
  roleEdits,
  setRoleEdits,
  permissionForms,
  setPermissionForms,
  showToast,
  refreshRoles
}) => {
  const [saving, setSaving] = useState(false);

  const submitRole = async (e) => {
    e.preventDefault();
    if (!roleForm.role_key || !roleForm.name) {
      showToast('error', 'Role key and name are required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        role_key: roleForm.role_key.trim(),
        name: roleForm.name.trim(),
        description: roleForm.description?.trim() || null
      };
      const { error } = await supabase.from('staff_roles').insert([payload]);
      if (error) throw error;
      setRoleForm({ role_key: '', name: '', description: '' });
      showToast('success', 'Role created');
      await refreshRoles();
    } catch (err) {
      console.error('create role error', err);
      showToast('error', err.message || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const saveRoleEdits = async (role) => {
    const edits = roleEdits[role.id];
    if (!edits) return;
    try {
      const { error } = await supabase
        .from('staff_roles')
        .update({
          name: edits.name.trim(),
          description: edits.description?.trim() || null
        })
        .eq('id', role.id);
      if (error) throw error;
      showToast('success', 'Role updated');
      setRoleEdits(prev => {
        const next = { ...prev };
        delete next[role.id];
        return next;
      });
      await refreshRoles();
    } catch (err) {
      console.error('update role error', err);
      showToast('error', err.message || 'Failed to update role');
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role ${role.role_key}?`)) return;
    try {
      const { error } = await supabase.from('staff_roles').delete().eq('id', role.id);
      if (error) throw error;
      showToast('success', 'Role removed');
      await refreshRoles();
    } catch (err) {
      console.error('delete role error', err);
      showToast('error', err.message || 'Failed to delete role');
    }
  };

  const submitPermission = async (roleKey) => {
    const form = permissionForms[roleKey] || { resource: '', action: '', constraint: '' };
    if (!form.resource || !form.action) {
      showToast('error', 'Resource and action are required');
      return;
    }
    try {
      const { error } = await supabase.from('role_permissions').insert([{
        role_key: roleKey,
        resource: form.resource.trim(),
        action: form.action.trim(),
        constraint_expression: form.constraint?.trim() || null
      }]);
      if (error) throw error;
      showToast('success', 'Permission added');
      setPermissionForms(prev => ({ ...prev, [roleKey]: { resource: '', action: '', constraint: '' } }));
      await refreshRoles();
    } catch (err) {
      console.error('permission add error', err);
      showToast('error', err.message || 'Failed to add permission');
    }
  };

  const deletePermission = async (permId) => {
    try {
      const { error } = await supabase.from('role_permissions').delete().eq('id', permId);
      if (error) throw error;
      showToast('success', 'Permission removed');
      await refreshRoles();
    } catch (err) {
      console.error('permission delete error', err);
      showToast('error', err.message || 'Failed to remove permission');
    }
  };

  const permissionsByRole = useMemo(() => {
    return rolePermissions.reduce((acc, perm) => {
      acc[perm.role_key] = acc[perm.role_key] || [];
      acc[perm.role_key].push(perm);
      return acc;
    }, {});
  }, [rolePermissions]);

  return (
    <div className="space-y-6">
      <form onSubmit={submitRole} className="bg-gray-800 border rounded p-4 space-y-3">
        <div className="text-lg font-medium">Create Role</div>
        <div className="grid md:grid-cols-3 gap-3">
          <input
            className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
            placeholder="Role key (lowercase)"
            value={roleForm.role_key}
            onChange={(e) => setRoleForm(prev => ({ ...prev, role_key: e.target.value.toLowerCase() }))}
          />
          <input
            className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
            placeholder="Name"
            value={roleForm.name}
            onChange={(e) => setRoleForm(prev => ({ ...prev, name: e.target.value }))}
          />
          <input
            className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
            placeholder="Description"
            value={roleForm.description}
            onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Create Role'}
        </button>
      </form>

      <div className="bg-gray-800 border rounded">
        <div className="border-b px-4 py-3 text-sm font-medium text-gray-300">
          Role Matrix
        </div>
        {rolesLoading ? (
          <div className="p-6">
            <LoadingSpinner message="Loading roles..." />
          </div>
        ) : (
          <div className="divide-y">
            {roles.length === 0 && (
              <div className="p-6 text-sm text-gray-400">No roles defined yet.</div>
            )}
            {roles.map(role => {
              const editState = roleEdits[role.id];
              const perms = permissionsByRole[role.role_key] || [];
              const permForm = permissionForms[role.role_key] || { resource: '', action: '', constraint: '' };
              return (
                <div key={role.id} className="p-4 space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="text-sm uppercase tracking-wide text-gray-400">{role.role_key}</div>
                      {editState ? (
                        <div className="flex flex-col md:flex-row md:items-center gap-2 mt-1">
                          <input
                            className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-sm"
                            value={editState.name}
                            onChange={(e) => setRoleEdits(prev => ({
                              ...prev,
                              [role.id]: { ...prev[role.id], name: e.target.value }
                            }))}
                          />
                          <input
                            className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-sm md:w-72"
                            value={editState.description || ''}
                            onChange={(e) => setRoleEdits(prev => ({
                              ...prev,
                              [role.id]: { ...prev[role.id], description: e.target.value }
                            }))}
                          />
                        </div>
                      ) : (
                        <div className="text-sm text-gray-300">
                          {role.name}
                          {role.description && <span className="text-gray-400"> — {role.description}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {editState ? (
                        <>
                          <button
                            onClick={() => saveRoleEdits(role)}
                            className="px-3 py-1 text-xs bg-green-600 text-white rounded"
                          >
                            Save
                          </button>
                          <button
                            onClick={() =>
                              setRoleEdits(prev => {
                                const next = { ...prev };
                                delete next[role.id];
                                return next;
                              })
                            }
                            className="px-3 py-1 text-xs border rounded"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setRoleEdits(prev => ({
                            ...prev,
                            [role.id]: { name: role.name, description: role.description || '' }
                          }))}
                          className="px-3 py-1 text-xs border rounded"
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => deleteRole(role)}
                        className="px-3 py-1 text-xs border rounded text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-700/50 border rounded p-3">
                    <div className="text-xs font-medium text-gray-400 mb-2">Permissions</div>
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-400 uppercase">
                          <th className="py-1 pr-4">Resource</th>
                          <th className="py-1 pr-4">Action</th>
                          <th className="py-1 pr-4">Constraint</th>
                          <th className="py-1 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {perms.map(perm => (
                          <tr key={perm.id} className="border-t border-gray-700">
                            <td className="py-2 pr-4 text-gray-200">{perm.resource}</td>
                            <td className="py-2 pr-4 text-gray-200">{perm.action}</td>
                            <td className="py-2 pr-4 text-gray-400">{perm.constraint_expression || '—'}</td>
                            <td className="py-2 text-right">
                              <button
                                onClick={() => deletePermission(perm.id)}
                                className="px-2 py-1 text-[11px] border rounded text-red-600 border-red-200 hover:bg-red-50"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                        {perms.length === 0 && (
                          <tr>
                            <td colSpan={4} className="py-2 text-gray-400">No permissions yet.</td>
                          </tr>
                        )}
                        <tr className="border-t border-gray-700">
                          <td className="py-2 pr-2">
                            <input
                              className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-xs w-full"
                              placeholder="Resource (e.g. leads)"
                              value={permForm.resource}
                              onChange={(e) => setPermissionForms(prev => ({
                                ...prev,
                                [role.role_key]: { ...prev[role.role_key], resource: e.target.value }
                              }))}
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <input
                              className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-xs w-full"
                              placeholder="Action (e.g. read)"
                              value={permForm.action}
                              onChange={(e) => setPermissionForms(prev => ({
                                ...prev,
                                [role.role_key]: { ...prev[role.role_key], action: e.target.value }
                              }))}
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <input
                              className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-xs w-full"
                              placeholder="Constraint (optional SQL)"
                              value={permForm.constraint || ''}
                              onChange={(e) => setPermissionForms(prev => ({
                                ...prev,
                                [role.role_key]: { ...prev[role.role_key], constraint: e.target.value }
                              }))}
                            />
                          </td>
                          <td className="py-2 text-right">
                            <button
                              onClick={() => submitPermission(role.role_key)}
                              className="px-3 py-1 text-xs bg-blue-600 text-white rounded"
                            >
                              Add
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const TeamsManager = ({
  teams,
  teamsLoading,
  teamForm,
  setTeamForm,
  teamEdits,
  setTeamEdits,
  staffOptions,
  teamMemberAdds,
  setTeamMemberAdds,
  showToast,
  refreshTeams,
  canManageTeams
}) => {
  const [saving, setSaving] = useState(false);
  const readOnly = !canManageTeams;

  const requireAdmin = () => {
    showToast('error', 'Admin access required for this action');
  };

  const submitTeam = async (e) => {
    e.preventDefault();
    if (!canManageTeams) {
      requireAdmin();
      return;
    }
    if (!teamForm.team_key || !teamForm.name) {
      showToast('error', 'Team key and name are required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        team_key: teamForm.team_key.trim().toLowerCase(),
        name: teamForm.name.trim(),
        description: teamForm.description?.trim() || null
      };
      const { error } = await supabase.from('staff_teams').insert([payload]);
      if (error) throw error;
      setTeamForm({ team_key: '', name: '', description: '' });
      showToast('success', 'Team created');
      await refreshTeams();
    } catch (err) {
      console.error('team create error', err);
      showToast('error', err.message || 'Failed to create team');
    } finally {
      setSaving(false);
    }
  };

  const saveTeamEdits = async (team) => {
    if (!canManageTeams) {
      requireAdmin();
      return;
    }
    const edits = teamEdits[team.id];
    if (!edits) return;
    try {
      const { error } = await supabase
        .from('staff_teams')
        .update({
          name: edits.name.trim(),
          description: edits.description?.trim() || null,
          is_active: edits.is_active
        })
        .eq('id', team.id);
      if (error) throw error;
      showToast('success', 'Team updated');
      setTeamEdits(prev => {
        const next = { ...prev };
        delete next[team.id];
        return next;
      });
      await refreshTeams();
    } catch (err) {
      console.error('team update error', err);
      showToast('error', err.message || 'Failed to update team');
    }
  };

  const deleteTeam = async (team) => {
    if (!canManageTeams) {
      requireAdmin();
      return;
    }
    if (!window.confirm(`Delete team ${team.team_key}?`)) return;
    try {
      const { error } = await supabase.from('staff_teams').delete().eq('id', team.id);
      if (error) throw error;
      showToast('success', 'Team deleted');
      await refreshTeams();
    } catch (err) {
      console.error('team delete error', err);
      showToast('error', err.message || 'Failed to delete team');
    }
  };

  const addMember = async (team) => {
    if (!canManageTeams) {
      requireAdmin();
      return;
    }
    const staffId = teamMemberAdds[team.team_key];
    if (!staffId) {
      showToast('error', 'Select a staff member first');
      return;
    }
    try {
      const payload = {
        team_key: team.team_key,
        staff_id: staffId,
        role_in_team: 'member',
        is_primary: false
      };
      const { error } = await supabase.from('team_members').insert([payload]);
      if (error) throw error;
      await supabase.rpc('admin_log_staff_event', {
        p_staff_id: staffId,
        p_event: 'team_joined',
        p_metadata: {
          team_key: team.team_key
        }
      }).catch(() => { });
      showToast('success', 'Member added');
      setTeamMemberAdds(prev => ({ ...prev, [team.team_key]: '' }));
      await refreshTeams();
    } catch (err) {
      console.error('add team member error', err);
      showToast('error', err.message || 'Failed to add team member');
    }
  };

  const updateMember = async (memberId, updates) => {
    if (!canManageTeams) {
      requireAdmin();
      return;
    }
    try {
      const { data, error } = await supabase.from('team_members').update(updates).eq('id', memberId).select('staff_id, team_key').single();
      if (error) throw error;
      if (data?.staff_id) {
        await supabase.rpc('admin_log_staff_event', {
          p_staff_id: data.staff_id,
          p_event: 'team_member_updated',
          p_metadata: {
            team_key: data.team_key,
            updates
          }
        }).catch(() => { });
      }
      showToast('success', 'Member updated');
      await refreshTeams();
    } catch (err) {
      console.error('update member error', err);
      showToast('error', err.message || 'Failed to update member');
    }
  };

  const removeMember = async (memberId) => {
    if (!canManageTeams) {
      requireAdmin();
      return;
    }
    if (!window.confirm('Remove member from team?')) return;
    try {
      const { data, error } = await supabase.from('team_members').delete().eq('id', memberId).select('staff_id, team_key').single();
      if (error) throw error;
      if (data?.staff_id) {
        await supabase.rpc('admin_log_staff_event', {
          p_staff_id: data.staff_id,
          p_event: 'team_left',
          p_metadata: {
            team_key: data.team_key
          }
        }).catch(() => { });
      }
      showToast('success', 'Member removed');
      await refreshTeams();
    } catch (err) {
      console.error('remove member error', err);
      showToast('error', err.message || 'Failed to remove member');
    }
  };

  const teamsWithMembers = useMemo(() => {
    return teams.map(team => {
      const members = team.team_members || [];
      return {
        ...team,
        team_members: members
      };
    });
  }, [teams]);


  return (
    <div className="space-y-6">
      {canManageTeams ? (
        <form onSubmit={submitTeam} className="bg-gray-800 border rounded p-4 space-y-3">
          <div className="text-lg font-medium">Create Team</div>
          <div className="grid md:grid-cols-3 gap-3">
            <input
              className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
              placeholder="Team key (lowercase)"
              value={teamForm.team_key}
              onChange={(e) => setTeamForm(prev => ({ ...prev, team_key: e.target.value.toLowerCase() }))}
            />
            <input
              className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
              placeholder="Name"
              value={teamForm.name}
              onChange={(e) => setTeamForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <input
              className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
              placeholder="Description"
              value={teamForm.description}
              onChange={(e) => setTeamForm(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Create Team'}
          </button>
        </form>
      ) : (
        <div className="bg-gray-800 border rounded p-4 text-sm text-gray-400">
          Team roster is read-only for your role. Contact an administrator to request changes.
        </div>
      )}

      <div className="bg-gray-800 border rounded">
        <div className="border-b px-4 py-3 text-sm font-medium text-gray-300">
          Teams &amp; Members
        </div>
        {teamsLoading ? (
          <div className="p-6">
            <LoadingSpinner message="Loading teams..." />
          </div>
        ) : (
          <div className="divide-y">
            {teamsWithMembers.length === 0 && (
              <div className="p-6 text-sm text-gray-400">No teams yet.</div>
            )}
            {teamsWithMembers.map(team => {
              const editState = canManageTeams ? teamEdits[team.id] : null;
              const memberSelect = canManageTeams ? (teamMemberAdds[team.team_key] || '') : '';
              const memberColSpan = canManageTeams ? 4 : 3;
              return (
                <div key={team.id} className="p-4 space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <div className="text-sm uppercase tracking-wide text-gray-400">{team.team_key}</div>
                      {editState ? (
                        <div className="flex flex-col md:flex-row md:items-center gap-2 mt-1">
                          <input
                            className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-sm"
                            value={editState.name}
                            onChange={(e) => setTeamEdits(prev => ({
                              ...prev,
                              [team.id]: { ...prev[team.id], name: e.target.value }
                            }))}
                          />
                          <input
                            className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-sm md:w-72"
                            value={editState.description || ''}
                            onChange={(e) => setTeamEdits(prev => ({
                              ...prev,
                              [team.id]: { ...prev[team.id], description: e.target.value }
                            }))}
                          />
                          <label className="text-xs flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={editState.is_active}
                              onChange={(e) => setTeamEdits(prev => ({
                                ...prev,
                                [team.id]: { ...prev[team.id], is_active: e.target.checked }
                              }))}
                            />
                            Active
                          </label>
                        </div>
                      ) : (
                        <>
                          <div className="text-sm text-gray-200">{team.name}</div>
                          {team.description && <div className="text-xs text-gray-400">{team.description}</div>}
                          <div className="text-xs text-gray-400">Status: {team.is_active ? 'Active' : 'Inactive'}</div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {canManageTeams ? (
                        editState ? (
                          <>
                            <button
                              onClick={() => saveTeamEdits(team)}
                              className="px-3 py-1 text-xs bg-green-600 text-white rounded"
                            >
                              Save
                            </button>
                            <button
                              onClick={() =>
                                setTeamEdits(prev => {
                                  const next = { ...prev };
                                  delete next[team.id];
                                  return next;
                                })
                              }
                              className="px-3 py-1 text-xs border rounded"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setTeamEdits(prev => ({
                              ...prev,
                              [team.id]: { name: team.name, description: team.description || '', is_active: !!team.is_active }
                            }))}
                            className="px-3 py-1 text-xs border rounded"
                          >
                            Edit
                          </button>
                        )
                      ) : (
                        <span className="text-xs text-gray-400">Read-only</span>
                      )}
                      {canManageTeams && (
                        <button
                          onClick={() => deleteTeam(team)}
                          className="px-3 py-1 text-xs border rounded text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-700/50 border rounded p-3 space-y-3">
                    <div className="text-xs font-medium text-gray-400">Members</div>
                    {canManageTeams && (
                      <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
                        <select
                          className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-sm md:w-72"
                          value={memberSelect}
                          onChange={(e) => setTeamMemberAdds(prev => ({ ...prev, [team.team_key]: e.target.value }))}
                        >
                          <option value="">Add member…</option>
                          {staffOptions.map(staff => (
                            <option key={staff.id} value={staff.id}>
                              {staff.name || staff.email} ({staff.role_key || 'role'})
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => addMember(team)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded"
                        >
                          Add
                        </button>
                      </div>
                    )}
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-400 uppercase">
                          <th className="py-1 pr-4">Name</th>
                          <th className="py-1 pr-4">Role in team</th>
                          <th className="py-1 pr-4">Primary</th>
                          {canManageTeams && <th className="py-1 text-right">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {team.team_members.length === 0 && (
                          <tr>
                            <td colSpan={memberColSpan} className="py-2 text-gray-400">No members yet.</td>
                          </tr>
                        )}
                        {team.team_members.map(member => (
                          <tr key={member.id} className="border-t border-gray-700">
                            <td className="py-2 pr-4 text-gray-200">
                              {member.staff_profiles?.name || member.staff_profiles?.email || member.staff_id}
                              <div className="text-[11px] text-gray-400">{member.staff_profiles?.role_key || 'role'}</div>
                            </td>
                            <td className="py-2 pr-4">
                              {readOnly ? (
                                <span>{member.role_in_team || 'Member'}</span>
                              ) : (
                                <input
                                  className="bg-gray-700 text-white border-gray-600 rounded px-2 py-1 text-xs"
                                  value={member.role_in_team || ''}
                                  onChange={(e) => updateMember(member.id, { role_in_team: e.target.value })}
                                />
                              )}
                            </td>
                            <td className="py-2 pr-4">
                              {readOnly ? (
                                <span>{member.is_primary ? 'Yes' : 'No'}</span>
                              ) : (
                                <label className="text-xs flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={member.is_primary}
                                    onChange={(e) => updateMember(member.id, { is_primary: e.target.checked })}
                                  />
                                  Primary
                                </label>
                              )}
                            </td>
                            {canManageTeams && (
                              <td className="py-2 text-right">
                                <button
                                  onClick={() => removeMember(member.id)}
                                  className="px-2 py-1 text-[11px] border rounded text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  Remove
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const InvitePanel = ({
  inviteForm,
  setInviteForm,
  inviteLoading,
  setInviteLoading,
  showToast,
  onSuccess,
  canManageInvites
}) => {
  const submitInvite = async (e) => {
    e.preventDefault();
    if (!canManageInvites) {
      showToast('error', 'Admin access required for this action');
      return;
    }
    if (!inviteForm.auth_user_id || !inviteForm.email || !inviteForm.role_key) {
      showToast('error', 'Auth user ID, email, and role are required');
      return;
    }
    try {
      setInviteLoading(true);
      const { data, error } = await supabase.rpc('admin_ensure_staff_profile', {
        p_auth_user_id: inviteForm.auth_user_id,
        p_email: inviteForm.email,
        p_name: inviteForm.name || inviteForm.email,
        p_role: inviteForm.role_key
      });
      if (error) throw error;
      const staffId =
        data?.profile?.id ||
        data?.id ||
        null;
      if (staffId) {
        await supabase.rpc('admin_log_staff_event', {
          p_staff_id: staffId,
          p_event: 'onboarded',
          p_metadata: {
            email: inviteForm.email,
            role_key: inviteForm.role_key,
            ensured_by: 'admin'
          }
        }).catch(() => { });
      }
      showToast('success', 'Staff profile ensured');
      setInviteForm({
        auth_user_id: '',
        email: '',
        name: '',
        role_key: inviteForm.role_key
      });
      await onSuccess();
    } catch (err) {
      console.error('ensure staff profile error', err);
      showToast('error', err.message || 'Failed to ensure staff profile');
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-900/20 border border-blue-700/50 rounded p-4 space-y-3 text-sm text-blue-100">
        <div className="flex items-start gap-2">
          <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <div className="text-lg font-medium text-white mb-2">Legacy Invite Workflow</div>
            <p className="mb-2">
              <strong className="text-blue-300">Note:</strong> For new staff, use the "Add Staff" button in the Directory tab for a streamlined one-step process.
            </p>
            <p className="mb-2">
              This section is for linking existing Supabase Auth users who were invited through the Supabase Auth Dashboard.
              Once they accept their invitation, paste their <code className="px-1 py-0.5 bg-gray-700 rounded text-xs">auth_user_id</code> below
              to create or update their staff profile.
            </p>
            <p className="text-blue-200 text-xs">
              This can also be used to update existing staff members' role, name, or email if they already have an auth account.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={submitInvite} className="bg-gray-800 border rounded p-4 space-y-3">
        <div className="text-lg font-medium">Ensure Staff Profile</div>
        <div className="grid md:grid-cols-2 gap-3">
          <input
            className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
            placeholder="Auth user ID (UUID)"
            value={inviteForm.auth_user_id}
            onChange={(e) => setInviteForm(prev => ({ ...prev, auth_user_id: e.target.value.trim() }))}
          />
          <input
            type="email"
            className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
            placeholder="Email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
          />
          <input
            className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
            placeholder="Full name (optional)"
            value={inviteForm.name}
            onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
          />
          <select
            className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
            value={inviteForm.role_key}
            onChange={(e) => setInviteForm(prev => ({ ...prev, role_key: e.target.value }))}
          >
            {['cro', 'docs', 'visa', 'agreements', 'education', 'manager', 'admin', 'guest'].map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
          disabled={inviteLoading}
        >
          {inviteLoading ? 'Ensuring…' : 'Ensure Staff Profile'}
        </button>
      </form>
    </div>
  );
};

export default StaffPage;

const ActivityLogPanel = ({
  logs,
  loading,
  filters,
  setFilters,
  page,
  setPage,
  pageSize,
  setPageSize,
  total,
  staffOptions,
  reload,
  showToast
}) => {
  const totalPages = Math.max(1, Math.ceil((total || logs.length) / pageSize));

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
    reload({ nextFilters: { ...filters, [key]: value }, page: 1, pageSize });
  };

  const handlePageChange = (nextPage) => {
    const clamped = Math.min(Math.max(1, nextPage), totalPages);
    setPage(clamped);
    reload({ page: clamped, pageSize, nextFilters: filters });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
    reload({ page: 1, pageSize: size, nextFilters: filters });
  };

  const clearFilters = () => {
    setFilters({ staff: 'all', event: 'all' });
    setPage(1);
    reload({ page: 1, pageSize, nextFilters: { staff: 'all', event: 'all' } });
  };

  return (
    <div className="space-y-4">
      <div className="bg-gray-800 border rounded p-4 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-lg font-medium">Staff Activity Logs</div>
            <p className="text-xs text-gray-400">
              Tracks auto-assignment, onboarding/offboarding, and team changes. Filters apply server-side.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-xs border rounded bg-gray-800 hover:bg-gray-700/50"
            >
              Reset filters
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <select
            className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
            value={filters.staff}
            onChange={(e) => handleFilterChange('staff', e.target.value)}
          >
            <option value="all">All staff</option>
            {staffOptions.map(staff => (
              <option key={staff.id} value={staff.id}>
                {staff.name || staff.email || staff.id}
              </option>
            ))}
          </select>
          <select
            className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
            value={filters.event}
            onChange={(e) => handleFilterChange('event', e.target.value)}
          >
            <option value="all">All events</option>
            {Array.from(new Set(logs.map(log => log.event))).map(event => (
              <option key={event} value={event}>{event}</option>
            ))}
          </select>
          <select
            className="bg-gray-700 text-white border-gray-600 rounded px-3 py-2 text-sm"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          >
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}/page</option>)}
          </select>
        </div>
      </div>

      <div className="bg-gray-800 border rounded">
        {loading ? (
          <div className="p-6">
            <LoadingSpinner message="Loading activity logs..." />
          </div>
        ) : (
          <div className="divide-y">
            {logs.length === 0 ? (
              <div className="p-6 text-sm text-gray-400">No activity captured for the selected filters.</div>
            ) : (
              logs.map(log => (
                <div key={log.id} className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-white">
                      <span className="px-2 py-1 text-xs bg-gray-200 rounded uppercase tracking-wide">
                        {log.event}
                      </span>
                      {log.staff_profiles ? (
                        <span>
                          {log.staff_profiles.name || log.staff_profiles.email}{' '}
                          <span className="text-xs text-gray-400">({log.staff_profiles.role_key})</span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">System</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                    </div>
                  </div>
                  {log.metadata && Object.keys(log.metadata || {}).length > 0 && (
                    <pre className="mt-2 text-xs bg-gray-700/50 border rounded p-2 text-gray-300 overflow-x-auto">
                      {JSON.stringify(log.metadata, null, 2)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {logs.length > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-400">
          <div>
            Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => handlePageChange(1)}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              First
            </button>
            <button
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <span>Page {page} / {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => handlePageChange(page + 1)}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => handlePageChange(totalPages)}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
